import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import type { Profile } from "@/lib/supabase";
import { getProfile } from "@/services/supabaseService";
import { clearAuthCache, ensureProfile } from "@/services/authService";

const AUTH_KEY = "jishi_auth_user";

/** 從 Supabase User 建立最小 fallback Profile（避免 UI 卡住） */
function createFallback(user: {
  id: string;
  email?: string | null;
  user_metadata?: Record<string, unknown>;
  created_at?: string;
}): Profile {
  const email = user.email || "";
  // 強制權限判定：admin 信箱直接賦予 admin 角色 + 核心分銷等級
  const isForcedAdmin = email === "admin@jishi-trade.com";
  return {
    id: user.id,
    name: (user.user_metadata?.name as string) || email.split("@")[0] || "用戶",
    email,
    phone: (user.user_metadata?.phone as string) || null,
    role: isForcedAdmin ? "admin" : ((user.user_metadata?.role as "admin" | "user") || "user"),
    level: isForcedAdmin ? "核心分銷" : ((user.user_metadata?.level as string) || "普通分銷"),
    balance: 0,
    pending_balance: 0,
    total_orders: 0,
    kyc_status: "未認證",
    created_at: user.created_at || new Date().toISOString(),
    avatar: null,
    notes: null,
    kyc_real_name: null,
    kyc_id_number: null,
    kyc_front_photo: null,
    kyc_back_photo: null,
    kyc_holding_photo: null,
    kyc_submitted_at: null,
    kyc_reviewed_at: null,
    kyc_admin_note: null,
    experience_active: false,
  };
}

/** 判斷是否為 Refresh Token 無效錯誤 */
function isRefreshTokenError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const msg = (err as { message?: string }).message || "";
  return (
    msg.includes("Invalid Refresh Token") ||
    msg.includes("Refresh Token Not Found") ||
    msg.includes("refresh_token") ||
    (err as { code?: string }).code === "refresh_token_not_found"
  );
}

/** 安全清除 auth 狀態 */
async function safeClearAuth(
  setUser: (u: null) => void,
  setIsLoading: (v: boolean) => void
) {
  try {
    await supabase.auth.signOut({ scope: "local" });
  } catch {
    // ignore signOut error
  }
  clearAuthCache();
  setUser(null);
  setIsLoading(false);
}

export function useAuth() {
  const [user, setUser] = useState<Profile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // ── Hydrate from existing session ─────────────────────────
  useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();

        if (sessionError && isRefreshTokenError(sessionError)) {
          console.warn("[useAuth] Refresh token invalid, clearing session:", sessionError.message);
          if (!cancelled) await safeClearAuth(setUser, setIsLoading);
          return;
        }

        const sessionUser = sessionData.session?.user;

        if (!sessionUser) {
          clearAuthCache();
          if (!cancelled) {
            setUser(null);
            setIsLoading(false);
          }
          return;
        }

        // 非阻塞：立即設定 fallback 用戶，頁面絕不卡住
        const fallback = createFallback(sessionUser);
        if (!cancelled) {
          setUser(fallback);
          setIsLoading(false);
        }

        // 背景非同步載入真實 profile（不阻塞主線程）
        getProfile(sessionUser.id).then(async (profile) => {
          if (cancelled) return;
          if (profile) {
            // 保護強制 admin：若 fallback 已強制 admin，不讓資料庫舊資料覆蓋
            if (fallback.email === "admin@jishi-trade.com") {
              setUser({ ...profile, role: "admin", level: "核心分銷" });
              localStorage.setItem(AUTH_KEY, JSON.stringify({ ...profile, role: "admin", level: "核心分銷" }));
            } else {
              setUser(profile);
              localStorage.setItem(AUTH_KEY, JSON.stringify(profile));
            }
          } else {
            const ensured = await ensureProfile(sessionUser.id, {
              ...sessionUser.user_metadata,
              email: sessionUser.email,
            });
            if (ensured && !cancelled) {
              if (ensured.email === "admin@jishi-trade.com") {
                const forced = { ...ensured, role: "admin" as const, level: "核心分銷" };
                setUser(forced);
                localStorage.setItem(AUTH_KEY, JSON.stringify(forced));
              } else {
                setUser(ensured);
                localStorage.setItem(AUTH_KEY, JSON.stringify(ensured));
              }
            }
          }
        });
      } catch (err) {
        console.error("[useAuth] init error:", err);
        if (isRefreshTokenError(err)) {
          if (!cancelled) await safeClearAuth(setUser, setIsLoading);
        } else {
          clearAuthCache();
          if (!cancelled) {
            setUser(null);
            setIsLoading(false);
          }
        }
      }
    }

    init();

    // ── Auth state listener ─────────────────────────────────
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      try {
        // 當 token 刷新失敗時，Supabase 會觸發 SIGNED_OUT 或拋出錯誤
        if (event === "SIGNED_OUT" || event === "USER_DELETED") {
          clearAuthCache();
          if (!cancelled) {
            setUser(null);
            setIsLoading(false);
          }
          return;
        }

        const u = session?.user;
        if (!u) {
          clearAuthCache();
          if (!cancelled) {
            setUser(null);
            setIsLoading(false);
          }
          return;
        }

        // 非阻塞：立即設定 fallback
        const fallback = createFallback(u);
        if (!cancelled) {
          setUser(fallback);
          setIsLoading(false);
        }

        // 背景載入真實 profile
        getProfile(u.id).then(async (profile) => {
          if (cancelled) return;
          if (profile) {
            // 保護強制 admin：若 fallback 已強制 admin，不讓資料庫舊資料覆蓋
            if (fallback.email === "admin@jishi-trade.com") {
              setUser({ ...profile, role: "admin", level: "核心分銷" });
              localStorage.setItem(AUTH_KEY, JSON.stringify({ ...profile, role: "admin", level: "核心分銷" }));
            } else {
              setUser(profile);
              localStorage.setItem(AUTH_KEY, JSON.stringify(profile));
            }
          } else {
            const ensured = await ensureProfile(u.id, {
              ...u.user_metadata,
              email: u.email,
            });
            if (ensured && !cancelled) {
              if (ensured.email === "admin@jishi-trade.com") {
                const forced = { ...ensured, role: "admin" as const, level: "核心分銷" };
                setUser(forced);
                localStorage.setItem(AUTH_KEY, JSON.stringify(forced));
              } else {
                setUser(ensured);
                localStorage.setItem(AUTH_KEY, JSON.stringify(ensured));
              }
            }
          }
        });
      } catch (err) {
        console.error("[useAuth] onAuthStateChange error:", err);
        if (isRefreshTokenError(err)) {
          if (!cancelled) await safeClearAuth(setUser, setIsLoading);
        }
      }
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  // ── Login ───────────────────────────────────────────────────
  const login = useCallback(
    async (email: string, password: string): Promise<Profile | null> => {
      clearAuthCache();

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw new Error(error.message);
      if (!data.user) throw new Error("登入成功但無法取得用戶資訊");

      // 立即建立 fallback，不阻塞後續跳轉
      const fallback = createFallback(data.user);
      setUser(fallback);
      localStorage.setItem(AUTH_KEY, JSON.stringify(fallback));

      // 背景同步真實 profile
      getProfile(data.user.id).then(async (profile) => {
        if (profile) {
          setUser(profile);
          localStorage.setItem(AUTH_KEY, JSON.stringify(profile));
        } else {
          const ensured = await ensureProfile(data.user.id, {
            ...data.user.user_metadata,
            email: data.user.email,
          });
          if (ensured) {
            setUser(ensured);
            localStorage.setItem(AUTH_KEY, JSON.stringify(ensured));
          }
        }
      });

      return fallback;
    },
    []
  );

  // ── Logout ──────────────────────────────────────────────────
  const logout = useCallback(async () => {
    await supabase.auth.signOut();
    clearAuthCache();
    setUser(null);
  }, []);

  // ── Update local user state ───────────────────────────────
  const updateUser = useCallback((patch: Partial<Profile>) => {
    setUser((prev) => {
      if (!prev) return null;
      const updated = { ...prev, ...patch };
      localStorage.setItem(AUTH_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  // ── Refresh user from Supabase ────────────────────────────
  const refreshUser = useCallback(async (): Promise<Profile | null> => {
    try {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();

      if (sessionError && isRefreshTokenError(sessionError)) {
        console.warn("[useAuth] refreshUser: Refresh token invalid, clearing session");
        await safeClearAuth(setUser, setIsLoading);
        return null;
      }

      const sessionUser = sessionData.session?.user;
      if (!sessionUser) {
        setUser(null);
        return null;
      }
      const profile = await getProfile(sessionUser.id);
      if (profile) {
        const isForcedAdmin = profile.email === "admin@jishi-trade.com";
        const updated = isForcedAdmin
          ? { ...profile, role: "admin" as const, level: "核心分銷" }
          : profile;
        setUser(updated);
        localStorage.setItem(AUTH_KEY, JSON.stringify(updated));
        return updated;
      }
      return null;
    } catch (err) {
      console.error("[useAuth] refreshUser error:", err);
      if (isRefreshTokenError(err)) {
        await safeClearAuth(setUser, setIsLoading);
      }
      return null;
    }
  }, []);

  // 管理員判定：僅限 role=admin 或硬編碼 admin 信箱
  const isAdmin =
    user?.role === "admin" ||
    user?.email === "admin@jishi-trade.com" ||
    false;

  return {
    user,
    isLoading,
    login,
    logout,
    updateUser,
    refreshUser,
    isAdmin,
    isLoggedIn: !!user,
  };
}